import React, { useState, useEffect } from 'react';
import { MapPin, Search, Loader2 } from 'lucide-react';
import { searchAddress } from '../../map/services/nominatim';

export const AddressSearch = ({ onLocationSelect, placeholder = "Search for a location..." }) => {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showResults, setShowResults] = useState(false);

    useEffect(() => {
        if (query.length < 3) {
            setResults([]);
            return;
        }

        const timeoutId = setTimeout(async () => {
            setLoading(true);
            try {
                const data = await searchAddress(query);
                setResults(data);
                setShowResults(true);
            } catch (error) {
                console.error('Search error:', error);
                setResults([]);
            } finally {
                setLoading(false);
            }
        }, 500); // Keep debounce for UI responsiveness

        return () => clearTimeout(timeoutId);
    }, [query]);

    const handleSelect = (result) => {
        const location = {
            lat: parseFloat(result.lat),
            lng: parseFloat(result.lon || result.lng),
            address: result.display_name
        };
        onLocationSelect(location);
        setQuery(result.display_name);
        setShowResults(false);
    };

    return (
        <div className="relative">
            <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground z-10" />
                <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onFocus={() => results.length > 0 && setShowResults(true)}
                    onBlur={() => setTimeout(() => setShowResults(false), 200)}
                    placeholder={placeholder}
                    className="w-full h-12 pl-12 pr-12 bg-zinc-100 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-gold/50 focus:ring-1 focus:ring-gold/50 transition-all font-medium"
                />
                {loading && (
                    <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gold animate-spin" />
                )}
            </div>

            {/* Results Dropdown */}
            {showResults && results.length > 0 && (
                <div className="absolute left-0 right-0 z-[100] mt-2 rounded-xl overflow-hidden border border-white/10 bg-[#1A1A1A] shadow-2xl max-h-68 overflow-y-auto">
                    {results.map((result, index) => (
                        <button
                            key={index}
                            onMouseDown={(e) => {
                                e.preventDefault();
                                handleSelect(result);
                            }}
                            onTouchStart={(e) => {
                                e.preventDefault();
                                handleSelect(result);
                            }}
                            onClick={() => handleSelect(result)}
                            className="w-full px-4 py-3 text-left hover:bg-white/10 transition-colors border-b border-white/5 last:border-0 flex items-start gap-3 cursor-pointer"
                        >
                            <MapPin className="w-4 h-4 text-gold mt-1 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                                <p className="text-foreground text-sm font-medium truncate">
                                    {result.display_name.split(',')[0]}
                                </p>
                                <p className="text-muted-foreground text-xs truncate">
                                    {result.display_name}
                                </p>
                            </div>
                        </button>
                    ))}
                </div>
            )}

            {/* No Results */}
            {showResults && !loading && query.length >= 3 && results.length === 0 && (
                <div className="absolute z-50 w-full mt-2 rounded-xl overflow-hidden border border-white/10 bg-[#1A1A1A] shadow-xl">
                    <div className="px-4 py-3 text-muted-foreground text-sm text-center">
                        No locations found. Try clicking on the map instead.
                    </div>
                </div>
            )}
        </div>
    );
};

export default AddressSearch;
